import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircleHeart, X, Send, Loader2 } from "lucide-react";
import { API } from "@/lib/data";

export const AIAssistant = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Hello, friend. I'm here if you'd like to talk — about faith, doubt, life, or anything on your heart. What's on your mind today?",
    },
  ]);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(
    `qa-${Math.random().toString(36).slice(2)}`
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async () => {
    const question = input.trim();
    if (!question || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: question }, { role: "assistant", text: "" }]);
    setLoading(true);

    try {
      const res = await fetch(`${API}/ai/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question, session_id: sessionId.current }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = {
                role: "assistant",
                text: copy[copy.length - 1].text + payload.delta,
              };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: "I'm having trouble responding right now. Please try again in a moment.",
        };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="ai-assistant-toggle"
        className="fixed bottom-24 right-6 z-[60] flex items-center gap-2 rounded-full bg-terracotta px-5 py-3.5 text-white shadow-lg transition-all hover:bg-terracotta-hover hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-forest"
      >
        {open ? <X size={22} /> : <MessageCircleHeart size={22} />}
        <span className="hidden sm:inline font-body text-sm font-medium">
          {open ? "Close" : "Ask the Pastor"}
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-40 right-4 sm:right-6 z-[60] w-[calc(100vw-2rem)] sm:w-[400px] h-[540px] max-h-[70vh] flex flex-col rounded-3xl bg-white shadow-2xl border border-black/5 overflow-hidden"
            data-testid="ai-assistant-panel"
          >
            <div className="bg-forest text-sand px-5 py-4">
              <p className="overline text-terracotta">AI Companion</p>
              <h4 className="font-heading text-2xl mt-0.5">A conversation with the Pastor</h4>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-sand-alt/40">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    data-testid={`ai-msg-${m.role}`}
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 font-body text-[15px] leading-relaxed ${
                      m.role === "user"
                        ? "bg-terracotta text-white rounded-br-sm"
                        : "bg-white text-ink border border-black/5 rounded-bl-sm"
                    }`}
                  >
                    {m.text || (loading && i === messages.length - 1 ? (
                      <Loader2 className="animate-spin" size={16} />
                    ) : null)}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-3 border-t border-black/5 bg-white">
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  rows={1}
                  placeholder="Share what's on your heart…"
                  data-testid="ai-assistant-input"
                  className="flex-1 resize-none rounded-2xl border border-black/10 bg-sand/60 px-4 py-2.5 font-body text-sm focus:outline-none focus:ring-2 focus:ring-terracotta max-h-28"
                />
                <button
                  onClick={send}
                  disabled={loading || !input.trim()}
                  data-testid="ai-assistant-send"
                  className="shrink-0 rounded-full bg-terracotta p-3 text-white transition-all hover:bg-terracotta-hover disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-forest"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
